import { SnapTolerance } from "@novorender/api";
import { ReadonlyVec3 } from "gl-matrix";
import { createContext, MutableRefObject } from "react";

/**
 * The kind of geometric feature a pick was snapped to.
 * `"none"` means the pick landed on a plain surface with no snap.
 */
export type PickingSnapType = "vertex" | "corner" | "tangent" | "midpoint" | "normal" | "none";

/**
 * Snap types that can be enabled for a picking session. `face` is intentionally excluded: since we
 * pick a single point, snapping to a face is equivalent to the raw surface hit (`snapType: "none"`).
 */
export type PickingSnapKey = Exclude<keyof SnapTolerance, "face">;

/**
 * Raw pick data produced by the picking session, before external-id resolution is attached.
 * This is what `onHover` receives; a committed pick resolves to the richer {@link PickingResult}.
 */
export interface PickingResultData {
    /** The snapped world-space coordinate (or the raw hit position when `snapType` is `"none"`). */
    position: ReadonlyVec3;
    /**
     * Surface normal at the raw `view.pick()` hit location. Only meaningful when `snapType` is
     * `"none"` (a plain surface pick); for snapped features (vertex/corner/edge/etc.) it is just the
     * normal under the cursor and does not describe the snapped feature itself.
     */
    normal: ReadonlyVec3;
    snapType: PickingSnapType;
    /** Internal object id of the picked geometry, or `null` when none. */
    internalObjectId: number | null;
    /**
     * World-space positions of the vertices adjacent to the snapped feature. Populated for
     * `"corner"` (the neighboring vertices of the edges meeting at the corner) and for
     * `"tangent"` / `"midpoint"` (the two endpoints of the snapped edge). `undefined` for other
     * snap types, and an empty array when the adjacent geometry is not a straight edge (e.g. arcs).
     */
    adjacentVertices?: ReadonlyVec3[];
}

/** Result of a committed pick (see {@link StartPickingOptions}). */
export interface PickingResult extends PickingResultData {
    /**
     * Lazily resolve the internal {@link PickingResultData.internalObjectId} to its external
     * (public) object id. Resolution can hit the network, so it runs only on demand and the result
     * is cached after the first call. Resolves to `null` when there is no picked object
     * (`internalObjectId` is `null`) or the object has no external id.
     */
    getObjectId(): Promise<string | null>;
}

/**
 * Info passed to `onHover` on every hover update. `null` when nothing is under the cursor.
 *
 * Unlike {@link PickingResult} this has no `getObjectId`: hover fires on every pointer move, so
 * resolving external ids here would invite a request per move. Resolve the id on the committed pick
 * instead, or map `internalObjectId` yourself if you really need it while hovering.
 */
export type PickingHoverInfo = PickingResultData;

/** Options for a `startPicking` session. */
export interface StartPickingOptions {
    /**
     * Per-type snap tolerances for the snap types enabled via {@link StartPickingOptions.snapTo}.
     * A type without an entry here falls back to the same default as the PointLine measure tool.
     * Ignored for snap types that are not enabled.
     */
    snapTolerance?: Pick<SnapTolerance, PickingSnapKey>;
    /**
     * Allowlist of snap types to consider. Every snap type is disabled by default; set a type to
     * `true` to enable it. With no `snapTo`, nothing snaps and every pick resolves as `"none"`.
     *
     * Note on edges: the engine snaps to straight edges via `segment` and to curved edges via
     * `edge`, so enable both to snap to all edges.
     */
    snapTo?: Partial<Record<PickingSnapKey, boolean>>;
    /** Called on every hover update with the current snap target, or `null` when there is none. */
    onHover?: (info: PickingHoverInfo | null) => void;
    /** Abort the session. The `startPicking` promise rejects with an `AbortError`. */
    abortSignal?: AbortSignal;
}

/** Internal state of an active picking session, shared between the bridge and canvas handlers. */
export interface PickingSession {
    tolerance: SnapTolerance;
    onHover?: (info: PickingResultData | null) => void;
    resolve: (result: PickingResultData) => void;
    reject: (reason?: unknown) => void;
}

export type LazyContextType = MutableRefObject<PickingSession | null>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LazyStateContext = createContext<LazyContextType>(undefined as any);
